import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import {
  FolderIcon,
  PencilSquareIcon,
  PlusIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import { debounce } from "lodash";
import { updateUrlWithException } from "../../../components/Utils";
import {
  createTemplateCategory,
  fetchTemplateCategories,
  fetchTemplates,
  updateTemplateCategory,
} from "../../../api/templates";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { PMDialog } from "../../../components/utils/PMDialog";

export const TemplateList = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [itemState, setItemState] = useState({
    data: [],
    totalCount: 0,
    isLoading: true,
    error: null,
  });

  const [controller, setController] = useState({
    per_page: 100,
    page: searchParams.get("page") ? searchParams.get("page") : 1,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "desc",
    sortBy: searchParams.get("sortBy")
      ? searchParams.get("sortBy")
      : "updated_at",
    template_category_id: searchParams.get("template_category_id") || "",
  });
  const [categoryState, setCategoryState] = useState({
    data: [],
    isLoading: true,
    error: null,
  });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    id: null,
    name: "",
    description: "",
    parent_id: "",
    active: true,
  });
  const [isSavingCategory, setIsSavingCategory] = useState(false);

  useEffect(() => {
    updateUrlWithException(
      {
        page: controller.page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        template_category_id: controller.template_category_id,
      },
      "filters"
    );
  }, [controller]);

  const loadCategories = useCallback(async () => {
    setCategoryState((prev) => ({ ...prev, isLoading: true }));
    try {
      const res = await fetchTemplateCategories({
        tree: true,
        include_templates: true,
      });
      const data = res?.data?.data || [];
      setCategoryState({ data, isLoading: false, error: null });
      setSelectedCategory((prev) => {
        if (!prev?.id) return prev;
        return findCategoryById(data, prev.id) || null;
      });
    } catch (error) {
      console.error(error);
      setCategoryState({ data: [], isLoading: false, error });
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const categoryOptions = useMemo(() => flattenCategories(categoryState.data), [categoryState.data]);

  const handleFiltersChange = (filters) => {
    setController({
      ...controller,
      page: 1,
      query: filters.query ?? controller.query,
      template_category_id: filters.categories?.[0] || "",
    });
  };

  const getTemplates = useCallback(async (currentController = controller) => {
    await fetchTemplates({
      query: currentController.query,
      page: currentController.page,
      per_page: currentController.per_page,
      sort: currentController.sort,
      sortBy: currentController.sortBy,
      template_category_id: currentController.template_category_id,
    })
      .then((res) => {
        setItemState({
          data: res?.data?.data || [],
          totalCount: res?.data?.totalCount || 0,
          isLoading: false,
          error: null,
        });
      })
      .catch((error) => {
        console.error(error);
        setItemState({
          data: [],
          totalCount: 0,
          isLoading: false,
          error,
        });
      });
  }, [controller]);

  const debouncedFetchTemplates = useMemo(
    () =>
      debounce((currentController) => {
        setItemState((prev) => ({ ...prev, isLoading: true }));
        getTemplates(currentController);
      }, 250),
    [getTemplates]
  );

  useEffect(() => {
    debouncedFetchTemplates(controller);
    return () => {
      debouncedFetchTemplates.cancel();
    };
  }, [controller, debouncedFetchTemplates]);

  const shownTemplates = filterTemplates(
    selectedCategory?.templates || itemState.data || [],
    controller.query
  );
  const uncategorizedCount = itemState.data.filter((template) => !template.template_category_id).length;

  const openNewCategoryDialog = () => {
    setCategoryForm({
      id: null,
      name: "",
      description: "",
      parent_id: selectedCategory?.id || "",
      active: true,
    });
    setCategoryDialogOpen(true);
  };

  const openEditCategoryDialog = (category) => {
    setCategoryForm({
      id: category.id,
      name: category.name || "",
      description: category.description || "",
      parent_id: category.parent_id || "",
      active: category.active !== false,
    });
    setCategoryDialogOpen(true);
  };

  const saveCategory = async (event) => {
    event.preventDefault();
    setIsSavingCategory(true);
    const payload = {
      ...categoryForm,
      parent_id: categoryForm.parent_id || null,
    };
    const response = categoryForm.id
      ? await updateTemplateCategory(payload)
      : await createTemplateCategory(payload);
    setIsSavingCategory(false);

    if (response?.errors) {
      setCategoryState((prev) => ({ ...prev, error: response.errors }));
      return;
    }

    setCategoryDialogOpen(false);
    await loadCategories();
  };

  return (
    <>
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-8 rounded-[2rem] border border-slate-200 bg-white px-6 py-6 shadow-sm">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                  <Squares2X2Icon className="h-4 w-4" />
                  {t("templates.hero.kicker")}
                </div>
                <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
                  {t("templates.title")}
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-600">
                  {t("templates.hero.subtitle")}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={openNewCategoryDialog}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                >
                  <FolderIcon className="h-4 w-4" />
                  {t("templates.categories.new")}
                </button>
                <Link
                  to="/dashboard/templates/new"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
                >
                  <PlusIcon className="h-4 w-4" />
                  {t("templates.new")}
                </Link>
              </div>
            </div>
          </div>

          <section className="mb-8 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-6 py-5">
              <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                {t("templates.filters.heading")}
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                {t("templates.filters.description")}
              </p>
            </div>
            <div className="space-y-5 px-6 py-6">
              <ModuleFilter
                disabled={itemState.isLoading || categoryState.isLoading}
                onFiltersChange={handleFiltersChange}
                initialFilters={{
                  query: controller.query,
                  categories: controller.template_category_id
                    ? [Number(controller.template_category_id)]
                    : [],
                }}
                hideStatusFilter={true}
                searchPlaceholder={t("templates.search_placeholder")}
                categoryTree={categoryState.data}
              />
            </div>
          </section>

          <section className="grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
            <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-6 py-5">
                <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {t("templates.categories.title")}
                </h2>
              </div>
              <div className="space-y-2 p-4">
                <CategoryButton
                  active={!selectedCategory}
                  name={t("templates.categories.all")}
                  count={itemState.totalCount}
                  onClick={() => setSelectedCategory(null)}
                />
                <CategoryButton
                  active={selectedCategory?.id === "uncategorized"}
                  name={t("templates.categories.uncategorized")}
                  count={uncategorizedCount}
                  onClick={() =>
                    setSelectedCategory({
                      id: "uncategorized",
                      name: t("templates.categories.uncategorized"),
                      templates: itemState.data.filter((template) => !template.template_category_id),
                    })
                  }
                />
                {categoryState.data.map((category) => (
                  <CategoryTree
                    key={category.id}
                    category={category}
                    selectedCategory={selectedCategory}
                    onSelect={setSelectedCategory}
                    onEdit={openEditCategoryDialog}
                  />
                ))}
              </div>
            </div>

            <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-col gap-4 border-b border-slate-200 px-6 py-6 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
                    <Squares2X2Icon className="h-4 w-4" />
                    {selectedCategory ? selectedCategory.name : t("templates.section.kicker")}
                  </div>
                  <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                    {selectedCategory ? t("templates.section.category_title") : t("templates.section.title")}
                  </h2>
                  <p className="mt-1 text-sm text-slate-600">
                    {selectedCategory ? t("templates.section.category_subtitle") : t("templates.section.subtitle")}
                  </p>
                </div>
                {selectedCategory?.id && selectedCategory.id !== "uncategorized" && (
                  <button
                    type="button"
                    onClick={() => openEditCategoryDialog(selectedCategory)}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                    {t("templates.categories.edit")}
                  </button>
                )}
              </div>
              <div className="divide-y divide-slate-100">
                {itemState.isLoading ? (
                  <div className="px-6 py-10 text-sm text-slate-500">{t("Loading...")}</div>
                ) : shownTemplates.length < 1 ? (
                  <div className="px-6 py-10 text-sm text-slate-500">
                    {t("templates.empty")}
                  </div>
                ) : (
                  shownTemplates.map((template) => (
                    <Link
                      key={template.id}
                      to={`/dashboard/templates/${template.id}`}
                      className="block px-6 py-4 transition hover:bg-slate-50"
                    >
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <h3 className="text-sm font-semibold text-slate-950">
                            {template.title}
                          </h3>
                          <p className="mt-1 text-xs text-slate-500">
                            {template.category?.name || t("templates.categories.uncategorized")}
                          </p>
                        </div>
                        <span className="text-xs font-semibold text-slate-500">
                          {t("Edit")}
                        </span>
                      </div>
                    </Link>
                  ))
                )}
              </div>
            </div>
          </section>
        </div>
      </div>

      <PMDialog
        open={categoryDialogOpen}
        onClose={() => setCategoryDialogOpen(false)}
        title={categoryForm.id ? t("templates.categories.edit") : t("templates.categories.new")}
        size="lg"
      >
        <form onSubmit={saveCategory} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">
              {t("templates.categories.name")}
            </label>
            <input
              type="text"
              value={categoryForm.name}
              onChange={(event) => setCategoryForm((prev) => ({ ...prev, name: event.target.value }))}
              required
              className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">
              {t("templates.categories.parent")}
            </label>
            <select
              value={categoryForm.parent_id || ""}
              onChange={(event) => setCategoryForm((prev) => ({ ...prev, parent_id: event.target.value }))}
              className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
            >
              <option value="">{t("templates.categories.no_parent")}</option>
              {categoryOptions
                .filter((category) => category.value !== categoryForm.id)
                .map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">
              {t("templates.categories.description")}
            </label>
            <textarea
              value={categoryForm.description}
              onChange={(event) => setCategoryForm((prev) => ({ ...prev, description: event.target.value }))}
              rows={3}
              className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setCategoryDialogOpen(false)}
              className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              disabled={isSavingCategory}
              className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
            >
              {isSavingCategory ? t("Saving...") : t("Save")}
            </button>
          </div>
        </form>
      </PMDialog>
    </>
  );
};

function CategoryButton({ active, name, count, onClick, onEdit, level = 0 }) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onClick}
        className={`flex min-w-0 flex-1 items-center justify-between rounded-2xl px-4 py-3 text-left text-sm transition ${
          active
            ? "bg-slate-900 font-semibold text-white shadow-sm"
            : "bg-slate-50 text-slate-700 hover:bg-slate-100"
        }`}
        style={{ marginLeft: level * 14 }}
      >
        <span className="truncate">{name}</span>
        <span className={active ? "text-white/70" : "text-slate-400"}>{count || 0}</span>
      </button>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
        >
          <PencilSquareIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function CategoryTree({ category, selectedCategory, onSelect, onEdit, level = 0 }) {
  return (
    <div className="space-y-2">
      <CategoryButton
        active={selectedCategory?.id === category.id}
        name={category.name}
        count={category.templates_count}
        level={level}
        onClick={() => onSelect(category)}
        onEdit={() => onEdit(category)}
      />
      {category.children?.map((child) => (
        <CategoryTree
          key={child.id}
          category={child}
          selectedCategory={selectedCategory}
          onSelect={onSelect}
          onEdit={onEdit}
          level={level + 1}
        />
      ))}
    </div>
  );
}

function flattenCategories(categories = [], level = 0) {
  return categories.flatMap((category) => [
    {
      value: category.id,
      label: `${"— ".repeat(level)}${category.name}`,
    },
    ...flattenCategories(category.children || [], level + 1),
  ]);
}

function findCategoryById(categories = [], id) {
  for (const category of categories) {
    if (String(category.id) === String(id)) return category;
    const child = findCategoryById(category.children || [], id);
    if (child) return child;
  }
  return null;
}

function filterTemplates(templates = [], query = "") {
  if (!query?.trim()) return templates;
  const normalizedQuery = query.toLowerCase();
  return templates.filter((template) =>
    `${template.title || ""} ${template.content || ""}`
      .toLowerCase()
      .includes(normalizedQuery)
  );
}
